/**
 * Граница жизни памяти разобранных ответов на клиенте (agroai-0e8.26,
 * журнал №1626, №1638).
 *
 * Память ответов `api/client` показывает запомненное только после того, как
 * сервер подтвердил ту же версию данных на ЭТОЙ навигации. Память поверх неё
 * — собранные наборы адаптеров (`once`), корпус кабинета, геометрия карты —
 * держится {@link VersionedMemo}: внутри одной навигации запись отдаётся как
 * есть, а на следующей навигации или после записи — только если сервер снова
 * подтвердил ту версию, под которой запись собиралась. Иначе — собирается
 * заново.
 *
 * Модуль без зависимостей: его импортируют и клиент, и слои памяти, а тесты,
 * подменяющие клиент, не должны терять подписку. Проверку версии клиент
 * регистрирует сам ({@link setVersionConfirmer}); без неё память живёт одну
 * навигацию — это строже, а не мягче.
 */

const listeners = new Set<() => void>();
let epoch = 0;
let confirmer: (() => Promise<string | null>) | null = null;

/** Подписать сброс памяти на смену «эпохи» данных (навигация, запись). */
export function onDataEpoch(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Эпоха сменилась: всё, что подписано, забывает разобранные ответы. */
export function bumpDataEpoch(): void {
  epoch += 1;
  for (const listener of listeners) listener();
}

/** Номер текущей эпохи данных. */
export function dataEpoch(): number {
  return epoch;
}

/** Клиент сообщает, как спросить у сервера текущую версию данных. */
export function setVersionConfirmer(confirm: () => Promise<string | null>): void {
  confirmer = confirm;
}

interface Entry<T> {
  task: Promise<T>;
  /** Версия, подтверждённая ДО начала сборки (null — неизвестна). */
  version: Promise<string | null>;
  epoch: number;
}

/**
 * Память собранных значений под версией данных.
 *
 * Почему версия берётся ДО сборки, а не после: данные, прочитанные после
 * подтверждения версии V, не старше V (отметка корпуса только растёт, а
 * память ответов отдаёт запомненное лишь под подтверждённой версией). Значит,
 * если позже сервер снова подтвердит ровно V, корпус не менялся, и собранное
 * — то же самое. Подтверждение, полученное после сборки, такого не гарантирует:
 * запись между чтением и подтверждением выдала бы старое под новой версией.
 */
export class VersionedMemo<T> {
  private entries = new Map<string, Entry<T>>();

  get(key: string, load: () => Promise<T>): Promise<T> {
    const now = epoch;
    const hit = this.entries.get(key);
    if (hit && hit.epoch === now) return hit.task;
    const entry: Entry<T> = { task: Promise.resolve() as Promise<never>, version: Promise.resolve(null), epoch: now };
    if (hit && confirmer) {
      // Новая навигация или запись: прежнее собранное отдаётся, только если
      // сервер снова подтвердил ту версию, под которой оно собиралось.
      const current = confirmer();
      entry.version = hit.version;
      entry.task = Promise.all([current, hit.version]).then(([version, built]) => {
        if (version !== null && version === built) return hit.task;
        entry.version = current;
        return load();
      });
    } else {
      // Первая сборка: версия подтверждается ДО чтения (см. выше).
      entry.version = confirmer ? confirmer() : Promise.resolve(null);
      entry.task = entry.version.then(() => load());
    }
    this.entries.set(key, entry);
    entry.task.catch(() => {
      // Неудача не запоминается: отказ сервера — состояние минуты.
      if (this.entries.get(key) === entry) this.entries.delete(key);
    });
    return entry.task;
  }

  delete(key: string): void {
    this.entries.delete(key);
  }

  clear(): void {
    this.entries.clear();
  }
}
